/**
 * Identifies which wire protocol an entity's backend controller expects for creation.
 * Derived from a backend controller audit (commons/rest-api/src/main/java, 2026-07-03).
 */
export const CREATE_PATTERNS = {
    // POST {endpoint} ignores/rejects the body; createBody has to go to
    // POST {endpoint}/createWithBody instead, which accepts everything, including
    // fields that become read-only once the entity is created.
    CREATE_WITH_BODY: 'CREATE_WITH_BODY',
    // POST {endpoint} (the base route) accepts createBody directly.
    DIRECT_BODY: 'DIRECT_BODY',
    // POST {endpoint} takes no body at all: createBody and body must be merged
    // and applied through a follow-up update(), every time.
    CREATE_THEN_UPDATE: 'CREATE_THEN_UPDATE',
    // Non-standard creation route (createWithAll, withName, ...): documented only, not wired into
    // the generic DataStore logic — needs a manual data store override wherever it's handled.
    MANUAL: 'MANUAL',
} as const

export type CreatePattern = typeof CREATE_PATTERNS[keyof typeof CREATE_PATTERNS]

// Entity -> creation pattern. Keys are singular camelCase like API_ENDPOINTS
// (not the Java PascalCase class names from the audit). This is the canonical
// reference for audited entities; only the ones whose data store constructor
// explicitly reads this map actually change behavior.
export const ENTITY_CREATE_PATTERNS: Record<string, CreatePattern> = {
    acquisitionProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    address: CREATE_PATTERNS.CREATE_WITH_BODY,
    album: CREATE_PATTERNS.CREATE_WITH_BODY,
    alias: CREATE_PATTERNS.CREATE_WITH_BODY,
    archeologyOperationProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    auditProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    borrowingProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    commissionProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    conditionReport: CREATE_PATTERNS.CREATE_WITH_BODY,
    deviceProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    diffusionProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    enteringDepositProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    expositionProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    fileRetentionProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    financingProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    genericProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    loanProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    locationRoomProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    outgoingDepositProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    outProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    programmingProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    restorationProject: CREATE_PATTERNS.CREATE_WITH_BODY,
    transcodingProject: CREATE_PATTERNS.CREATE_WITH_BODY,

    alert: CREATE_PATTERNS.DIRECT_BODY,
    batch: CREATE_PATTERNS.DIRECT_BODY,
    budgetItem: CREATE_PATTERNS.DIRECT_BODY,
    budgetTree: CREATE_PATTERNS.DIRECT_BODY,
    commissionProjectVote: CREATE_PATTERNS.DIRECT_BODY,
    domainSearch: CREATE_PATTERNS.DIRECT_BODY,
    formatter: CREATE_PATTERNS.DIRECT_BODY,
    group: CREATE_PATTERNS.DIRECT_BODY,
    linkableItem: CREATE_PATTERNS.DIRECT_BODY,
    savedSearch: CREATE_PATTERNS.DIRECT_BODY,
    statisticalQuery: CREATE_PATTERNS.DIRECT_BODY,
    user: CREATE_PATTERNS.DIRECT_BODY,
    work: CREATE_PATTERNS.DIRECT_BODY,

    accessPoint: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    archeologicalFurniture: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    archive: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    asset: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    audioFileRecord: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    audioTranscodingConfig: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    authority: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    biblioWork: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    classificationRank: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    collection: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    createFolderAndMoveMedia: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    determination: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    documentFileRecord: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    documentVignetteTranscodingConfig: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    domain: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    domainContext: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    editingSkinJob: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    editionReport: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    entityMenu: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    entry: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    event: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    exportedContent: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    exportJoconde: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    exportRecolnat: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    exportTemplate: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    filmWork: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    harvest: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    imageFileRecord: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    institution: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    insuranceEstimation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    liveShowExpression: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    liveShowWork: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    loanConsultation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    location: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    markSignature: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    metadataSkinJob: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    mission: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    missionPattern: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    museumWork: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericAccessoryItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericAccessoryManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericBiblioItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericBiblioManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericContainerItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericContainerManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericFilmItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericFilmManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericMuseumItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    numericMuseumManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    orderSubscription: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    output: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalAccessoryItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalAccessoryManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalBiblioItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalBiblioManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalContainerItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalContainerManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalFilmItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalFilmManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalMuseumItem: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    physicalMuseumManifestation: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    preEntry: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    process: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    propertyRights: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    recolement: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    registrationUnit: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    rejectedSkinJob: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    site: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    specimen: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    specimenClassification: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    specimenCollection: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    tag: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    task: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    taskPattern: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    thematicCollection: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    thesaurus: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    thesaurusTerm: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    town: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    transcodingFileRecord: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    transcodingSkinJob: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    uploadSkinJob: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    videoFileRecord: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    videoTranscodingConfig: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    videoVignetteTranscodingConfig: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    webExport: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    webExportPage: CREATE_PATTERNS.CREATE_THEN_UPDATE,
    workspace: CREATE_PATTERNS.CREATE_THEN_UPDATE,

    // Non-standard routes, not yet handled
    expression: CREATE_PATTERNS.MANUAL,
    intervention: CREATE_PATTERNS.MANUAL,
    media: CREATE_PATTERNS.MANUAL,
    movement: CREATE_PATTERNS.MANUAL,
    printableReport: CREATE_PATTERNS.MANUAL,
    skosCollection: CREATE_PATTERNS.MANUAL,
    workspaceMenu: CREATE_PATTERNS.MANUAL,
}
